import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { Chip, statusTone } from '../../components/ui/Chip';
import { Avatar } from '../../components/ui/Avatar';
import { DivisionTag } from '../../components/ui/DivisionTag';
import { EmptyState } from '../../components/ui/EmptyState';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { NewMenu } from '../../components/ui/NewMenu';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { useAssistenteStore } from '../../stores/assistente.store';
import { chiHaScritto } from '../assistente/registroApi';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate, fmtDateShort, euro, fmtPct } from '../../lib/utils';
import { ACTIVE_PROJECT_STATUSES, countActiveProjects, lastContactDate } from './clientMetrics';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ContactDetailMobile } from '../contacts/ContactDetailMobile';
import { leggibile } from '../../components/mobile/MobileKit';
import './crm.css';

interface ContactDTO {
  id: string; fullName: string; role: string | null; email: string | null; phone: string | null; isPrimary: boolean;
}

interface ClientFull {
  id: string;
  legalName: string;
  shortName: string;
  type: string;
  vat: string | null;
  sector: string | null;
  status: string;
  sinceYear: number | null;
  addressLine: string | null;
  city: string | null;
  zip: string | null;
  province: string | null;
  website: string | null;
  pec: string | null;
  email: string | null;
  phone: string | null;
  stripeCustomerId: string | null;
  accountManager: { firstName: string; lastName: string; initials: string } | null;
  tags: { id: string; name: string }[];
  contacts: ContactDTO[];
  projects: Array<{ id: string; code: string; name: string; division: string; phase: string; status: string; revenue: string; cost: string; marginThreshold: string; deadline: string | null; progress: number; pm: { firstName: string; lastName: string; initials: string } | null }>;
  invoices: Array<{ id: string; number: string | null; project: { code: string } | null; issueDate: string | null; dueDate: string | null; paidDate: string | null; status: string; total: string; subtotal: string; vat: string; paymentMethod: string | null }>;
  // aiActionId (29/10/2026, fase 2D): la riga l'ha scritta MUNE per l'autore, «tramite MUNE».
  communications: Array<{ id: string; kind: string; subject: string; body: string | null; tag: string | null; occurredAt: string; author: any; durationMin: number | null; project: { code: string } | null; aiActionId?: string | null }>;
  /** Totali su TUTTE le fatture, calcolati dal server: `invoices` qui sopra sono solo le ultime 20. */
  invoiceStats: { toCollect: number; overdue: number; paid: number; paidYtd: number; count: number };
  /** Lo stesso soggetto come fornitore, quando ci vende oltre che comprare. */
  supplierProfiles: Array<{ id: string; fullName: string; isPerson: boolean }>;
}

interface ClientNoteDTO {
  id: string;
  body: string;
  pinned: boolean;
  createdAt: string;
  author: { firstName: string; lastName: string; initials: string } | null;
  /** 29/10/2026, fase 2D: la nota l'ha scritta MUNE per l'autore, «tramite MUNE». */
  aiActionId?: string | null;
}

/** «Andrea Anfuso», «Andrea Anfuso, tramite MUNE», «Sistema» se non c'è una persona. */
const autoreNota = (n: ClientNoteDTO) =>
  chiHaScritto(n.author ? `${n.author.firstName} ${n.author.lastName}` : null, n.aiActionId, 'Sistema');

interface ClientMarginDTO {
  revenue: number; cost: number; marginAbs: number; marginPct: number;
  paidInvoicesTotal: number; projectsCount: number; activeProjectsCount: number;
}

interface DocumentDTO {
  id: string; name: string; filename: string; mime: string; size: number; docGroup: string; version: string | null; createdAt: string;
  uploader: { firstName: string; lastName: string; initials: string } | null;
}

// Ordine per frequenza d'uso CRM: la relazione (diario) prima dell'archivio (documenti)
const TABS = ['overview', 'progetti', 'fatture', 'diario', 'documenti'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = {
  overview: 'In breve',
  progetti: 'Progetti',
  fatture: 'Fatture',
  diario: 'Diario',
  documenti: 'Documenti',
};

/**
 * Sul telefono la scheda estesa del cliente sarebbe un doppione della scheda
 * contatto (stesso nome, stessi soldi, stesse fatture): lì si apre quella,
 * con in più le note del cliente (mobile.md §12.10). Sul computer resta questa.
 */
export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();
  if (!id) return <Navigate to="/crm" replace />;
  return isMobile ? <div className="page"><ContactDetailMobile kind="client" id={id} /></div> : <ClientDetailDesktop />;
}

function ClientDetailDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [tab, setTab] = useState<Tab>('overview');

  const delClient = useMutation({
    mutationFn: () => api.delete(`/clients/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.ok('Cliente eliminato', c?.legalName);
      navigate('/crm');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => api.get<ClientFull>(`/clients/${id}`),
    enabled: !!id,
    retry: false,
  });

  const { data: margin } = useQuery({
    queryKey: ['client-margin', id],
    queryFn: () => api.get<ClientMarginDTO>(`/clients/${id}/margin`),
    enabled: !!id && showMargin,
  });

  if (!id) return <Navigate to="/crm" replace />;
  if (isError) return (
    <div className="page">
      <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: 'Cliente' }]} />
      <div className="max-w-[480px] mx-auto py-20 text-center">
        <h1 className="t-h1">Cliente non trovato</h1>
        <div className="mute mt-2">L'ID richiesto non esiste o non hai i permessi per vederlo.</div>
      </div>
    </div>
  );
  // header="chips" e non "avatar": la scheda cliente l'avatar non ce l'ha, e il
  // cerchio da 56px dello scheletro spostava il titolo di 68px per poi
  // rimetterlo a posto all'arrivo dei dati. kpis={2} perche' la fascia dei
  // numeri qui ne ha due, non quattro.
  if (isLoading || !c) return <DetailSkeleton header="chips" kpis={2} />;

  // Health strip — i totali arrivano dal server, calcolati su TUTTE le fatture.
  // Sommarli qui dalle 20 caricate per la tabella faceva dire "0 di scaduto" a
  // clienti che ne avevano decine di migliaia.
  const inv = c.invoiceStats;
  const activeProjects = countActiveProjects(c.projects);
  const lastContact = lastContactDate(c.communications);

  return (
    // m-detail: sotto i 768px la scheda e' una colonna sola e le griglie a due
    // colonne collassano (mobile.md §7). Sul computer non dichiara niente.
    <div className="page m-detail">
      <Breadcrumb items={[{ label: 'Contatti', to: '/crm' }, { label: c.shortName }]} />
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora che si
            disegna; detail-meta e chip stato restano sotto come prima ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: la ragione sociale, tronca se lunga */}
          <h1 className="ed-title truncate" title={c.legalName}>{leggibile(c.legalName)}</h1>
          {/* L2 meta-indice: UNA sola riga arretrata (P.IVA · settore · anzianità · responsabile).
              Solo i pezzi che esistono davvero: su un'anagrafica arrivata da
              FattureInCloud settore, anno e responsabile sono quasi sempre
              vuoti, e "05959100875 · — · dal — · Resp. —" fa sembrare rotta la
              scheda. E' la stessa regola che gia' applica la scheda contatto. */}
          <div className="detail-meta mt-2 truncate">
            {[
              c.vat ? <span key="vat" className="mono">{c.vat}</span> : null,
              c.sector ? <span key="sector">{c.sector}</span> : null,
              c.sinceYear ? <span key="since">dal {c.sinceYear}</span> : null,
              c.accountManager ? (
                <span key="am"><span className="mute-2 mr-1.5">Resp.</span><span className="v">{c.accountManager.firstName[0]}. {c.accountManager.lastName}</span></span>
              ) : null,
            ]
              .filter(Boolean)
              // I puntini si mettono FRA i pezzi rimasti, non accanto a ognuno:
              // scritti a mano lasciavano un "·" davanti alla riga ogni volta
              // che il pezzo prima mancava.
              .flatMap((n, i) => (i === 0 ? [n] : [<span key={`sep${i}`} className="mute-2">·</span>, n]))}
          </div>
          {/* Stato: riga dedicata SOTTO, mai a competere con il nome in cima */}
          <div className="flex gap-1.5 mt-2.5 items-center">
            <Chip tone={statusTone(c.status)} dot>{c.status.replace('_', ' ').toLowerCase()}</Chip>
            {/* Il soggetto ci fattura anche: la scheda fornitore resta separata,
                qui c'è solo il rimando, così non si perde il pezzo di rapporto
                che vive in Amministrazione. */}
            {c.supplierProfiles.map((s) => (
              <button key={s.id} type="button" className="chip-label" onClick={() => navigate(`/suppliers/${s.id}`)} title={`Scheda fornitore · ${s.fullName}`}>
                anche fornitore →
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 items-center pb-1">
          <DesktopOnly
            cosa="La modifica della scheda e la creazione di progetti o documenti"
            perche="sono attività complete da computer; dal telefono puoi chiamare, scrivere e aggiungere subito una nota."
          >
          <>
          <NewMenu
            label="Nuovo"
            groups={[
              {
                label: 'Per questo cliente',
                items: [
                  { icon: 'cube',    title: 'Progetto',      sub: 'Cosa si vende e quando si consegna',           onClick: () => openModal('newProject', { clientId: c.id }) },
                  { icon: 'file',    title: 'Preventivo',    sub: 'Il cliente lo firma online',   onClick: () => openModal('newQuote', { clientId: c.id }) },
                  { icon: 'invoice', title: 'Fattura',       sub: 'Emessa con Fatture in Cloud',   onClick: () => openModal('newInvoice', { clientId: c.id }) },
                  { icon: 'mail',    title: 'Comunicazione', sub: 'Una chiamata, una mail, un incontro',        onClick: () => openModal('newComm', { clientId: c.id }) },
                  { icon: 'pin',     title: 'Nota',          sub: 'Una cosa da ricordare, solo per noi',          onClick: () => openModal('newClientNote', { clientId: c.id }) },
                  { icon: 'users',   title: 'Referente',     sub: 'Una persona da sentire in azienda',       onClick: () => openModal('newContact', { clientId: c.id }) },
                  { icon: 'upload',  title: 'Documento',     sub: 'Un file da tenere sulla scheda',    onClick: () => openModal('uploadDoc', { scope: 'client', scopeId: c.id }) },
                ],
              },
            ]}
          />
          <ActionsMenu
            variant="ghost"
            items={[
              {
                key: 'edit',
                label: 'Modifica scheda',
                icon: 'settings',
                onClick: () => openModal('newClient', { clientId: c.id }),
              },
              {
                // La scheda memoria dell'Assistente (PIANO.md, sezione 5): solo Amministratori.
                key: 'memoria',
                label: 'Cosa sa MUNE',
                icon: 'message',
                hidden: user?.role.key !== 'SUPER_ADMIN',
                onClick: () => useAssistenteStore.getState().apriMemoria({ tipo: 'cliente', id: c.id, nome: c.legalName }),
              },
              {
                key: 'stripePortal',
                label: 'Apri il portale Stripe del cliente',
                icon: 'link',
                hidden: !c.stripeCustomerId,
                onClick: async () => {
                  try {
                    const { url } = await api.get<{ url: string }>(`/services/stripe/customer-portal/${c.id}`);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (err: any) {
                    toast.alert('Errore portale fatturazione', err?.message ?? 'Riprova');
                  }
                },
              },
              {
                key: 'attachSubscription',
                label: 'Collega un abbonamento',
                icon: 'link',
                onClick: () => openModal('attachSubscription', { clientId: c.id, clientName: c.legalName }),
              },
              {
                key: 'export',
                label: 'Esporta scheda',
                icon: 'export',
                onClick: () => openModal('export', { what: 'Scheda cliente' }),
              },
              {
                key: 'delete',
                label: 'Elimina cliente',
                icon: 'trash',
                danger: true,
                onClick: async () => {
                  if (await confirmDialog({
                    title: `Eliminare ${c.legalName}?`,
                    body: 'Il cliente sparisce dal CRM. Progetti e fatture collegati restano nel sistema. Operazione reversibile dal database.',
                    danger: true,
                    confirmLabel: 'Elimina cliente',
                  })) delClient.mutate();
                },
              },
            ]}
          />
          </>
          </DesktopOnly>
        </div>
      </div>

      {/* Health strip di sintesi: i due numeri vivono NUDI sul foglio (stat-band),
          ognuno apre il tab che lo dettaglia (Fatture / Diario).
          Rosso solo per stati reali: il delta si accende se c'è scaduto. */}
      <div className="stat-band">
        <div className="st-click" role="button" tabIndex={0} onClick={() => setTab('fatture')} onKeyDown={(e) => { if (e.key === 'Enter') setTab('fatture'); }}>
          <div className="st-label">Ci deve</div>
          <div className="st-value"><span className="u">€ </span><AnimatedNumber value={inv.toCollect} format={fmtEur} /></div>
          <div className={`st-delta ${inv.overdue > 0 ? 'text-alert' : ''}`}>{inv.overdue > 0 ? `${euro(inv.overdue)} già scaduti` : inv.toCollect > 0 ? 'niente di scaduto' : 'non ci deve niente'}</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => setTab('diario')} onKeyDown={(e) => { if (e.key === 'Enter') setTab('diario'); }}>
          <div className="st-label">Progetti in corso</div>
          <div className="st-value"><AnimatedNumber value={activeProjects} /></div>
          <div className="st-delta">{lastContact ? 'ultimo contatto il ' + fmtDate(lastContact) : 'nessun contatto scritto nel diario'}</div>
        </div>
      </div>

      <div className="page-tabs">
        {TABS.map((t) => {
          // Counter solo dove il dato è già in mano (i documenti vivono in una query del tab)
          const count = t === 'progetti' ? c.projects.length
            : t === 'fatture' ? c.invoiceStats.count
            : t === 'diario' ? c.communications.length
            : null;
          return (
            <div key={t} className="page-tab" aria-selected={tab === t} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
              {count != null && count > 0 && <span className="c">{count}</span>}
            </div>
          );
        })}
      </div>

      {tab === 'overview'  && <OverviewTab c={c} showMargin={showMargin} margin={margin ?? null} setTab={setTab} />}
      {tab === 'progetti'  && <ProjectsTab c={c} showMargin={showMargin} />}
      {tab === 'fatture'   && <InvoicesTab c={c} />}
      {tab === 'documenti' && <DocumentsTab clientId={c.id} />}
      {tab === 'diario'    && <DiaryTab c={c} />}

      {/* Telefono: il gesto veloce che mobile.md §0 mette fra quelli da fare
          dal telefono — annotare una cosa appena detta al telefono, prima di
          dimenticarla. Il menu "Nuovo" resta in cima: e' una tendina che
          scende, e in una barra incollata al fondo si aprirebbe fuori schermo.
          Il modale, sotto i 768, sale gia' dal basso da solo (mobile.css). */}
      <div className="m-actionbar m-only">
        <button className="btn btn-primary" onClick={() => openModal('newClientNote', { clientId: c.id })}>
          <Icon name="pin" /> Nuova nota
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// OVERVIEW
// =====================================================================
function OverviewTab({ c, showMargin, margin, setTab }: { c: ClientFull; showMargin: boolean; margin: ClientMarginDTO | null; setTab: (t: Tab) => void }) {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);

  return (
    <div className="col gap-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)' }}>
      {/* min-w-0 su tutte e due le colonne, come fa gia' la scheda
          professionista: senza, la colonna non scende sotto la larghezza
          minima del suo contenuto e la porta fuori dalla griglia. Misurato a
          390: la colonna era 411px dentro 358, e trascinava fuori anche
          l'altra. Sul computer non cambia niente, la griglia e' larga. */}
      <div className="col gap-8 min-w-0">
        {/* Anteprima NUDA sul foglio: kicker + conteggio mono, CTA a destra, tabella full-bleed */}
        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-baseline gap-2">
              <span className="tk-sez-t">Progetti</span>
              <span className="mono mute text-[12px]">{c.projects.length}</span>
            </div>
            {c.projects.length > 0 && (
              <button className="text-text text-[13px] crm-hit" onClick={() => setTab('progetti')}>Vedi tutti →</button>
            )}
          </div>
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                <th>Progetto</th><th>Divisione</th><th>Fase</th>
                <th className="num">Ricavi</th>
                {showMargin && <th className="num">Margine</th>}
                <th className="num">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {c.projects.slice(0, 3).map((p) => {
                const rev = Number(p.revenue) || 0;
                const cost = Number(p.cost) || 0;
                const pct = rev > 0 ? +(((rev - cost) / rev) * 100).toFixed(1) : 0;
                return (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                    {/* Nome + codice su due righe: il codice resta leggibile ma arretra */}
                    <td data-m="hd" className="cell-2l"><div className="p">{p.name}</div><div className="s mono">{p.code}</div></td>
                    <td data-m="key" data-l="Divisione"><DivisionTag division={p.division as any} /></td>
                    <td data-m="key" data-l="Fase"><Chip tone={statusTone(p.status)} dot>{p.phase}</Chip></td>
                    <td data-m="key" data-l="Ricavi" className="num">{euro(rev)}</td>
                    {showMargin && <td data-m="key" data-l="Margine" className={`num tbl-metric ${cost > 0 && pct < 20 ? 'text-alert' : 'accent'}`} title={cost > 0 ? undefined : 'Nessun costo registrato su questo progetto: il margine non si può calcolare'}>{cost > 0 ? fmtPct(pct, 1) : <span className="mute-2">senza costi</span>}</td>}
                    <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(p.deadline)}</td>
                  </tr>
                );
              })}
              {/* Niente text-[13px] sul <td>: .tbl tbody td { font-size: 14px }
                  ha specificita' 0-1-2 e l'utility 0-1-0, quindi non ha mai
                  fatto niente. Era codice morto, non una scelta. */}
              {!c.projects.length && <tr><td colSpan={showMargin ? 6 : 5} className="text-center py-8 mute">Nessun progetto</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-baseline gap-2">
              <span className="tk-sez-t">Ultime fatture</span>
              <span className="mono mute text-[12px]">{c.invoiceStats.count}</span>
            </div>
            {c.invoices.length > 0 && (
              <button className="text-text text-[13px] crm-hit" onClick={() => setTab('fatture')}>Vedi tutte →</button>
            )}
          </div>
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr><th>Numero</th><th>Progetto</th><th>Emissione</th><th>Scadenza</th><th>Stato</th><th className="num">Importo</th></tr>
            </thead>
            <tbody>
              {c.invoices.slice(0, 3).map((i) => (
                <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}>
                  <td data-m="hd" className="mono">{i.number ?? 'BOZZA'}</td>
                  <td data-m="key" data-l="Progetto" className="mono mute-2">{i.project?.code ?? '—'}</td>
                  <td data-m="key" data-l="Emissione" className="mono mute">{fmtDate(i.issueDate)}</td>
                  <td data-m="key" data-l="Scadenza" className="mono mute">{fmtDate(i.dueDate)}</td>
                  <td data-m="key" data-l="Stato"><Chip tone={statusTone(i.status)} dot>{String(i.status).replace('_', ' ').toLowerCase()}</Chip></td>
                  <td data-m="key" data-l="Importo" className="num">{euro(i.total)}</td>
                </tr>
              ))}
              {!c.invoices.length && <tr><td colSpan={6} className="text-center py-8 mute">Nessuna fattura</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="ed-section">
          <div className="data-hd">
            <div className="flex items-baseline gap-2">
              <span className="tk-sez-t">Chiamate, mail e incontri</span>
              <span className="mono mute text-[12px]">{c.communications.length}</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Ghost: l'unica primaria della vista è il "+ Nuovo" nell'header pagina */}
              <button className="btn btn-sm btn-ghost" onClick={() => openModal('newComm', { clientId: c.id })}>
                <Icon name="plus" size={12} /> Nuova
              </button>
              {c.communications.length > 0 && (
                <button className="text-text text-[13px] crm-hit" onClick={() => setTab('diario')}>Vedi tutte →</button>
              )}
            </div>
          </div>
          <div>
            {c.communications.slice(0, 3).map((com) => (
              <div key={com.id} className="grid items-center gap-2.5 py-2.5 border-b border-line last:border-0" style={{ gridTemplateColumns: '70px 24px 1fr 80px' }}>
                <div className="mono mute text-[12px]">{fmtDateShort(com.occurredAt)}</div>
                {/* Qui c'è posto solo per le iniziali: il nome, anche «tramite MUNE» (2D), al passaggio. */}
                {com.author && <Avatar initials={com.author.initials} size={20} name={chiHaScritto(`${com.author.firstName} ${com.author.lastName}`, com.aiActionId)} />}
                <div className=" truncate">{com.subject}</div>
                <div className="text-right"><span className="t-micro" style={{ letterSpacing: '0.08em' }}>{com.kind.toLowerCase()}</span></div>
              </div>
            ))}
            {!c.communications.length && <div className="py-8 text-center text-[13px] mute">Nessuna comunicazione</div>}
          </div>
        </div>
      </div>

      <div className="col min-w-0">
        {/* Colonna laterale: kicker-blocks NUDI sul foglio, separati da hairline (ed-divider).
            Solo il margin-panel resta card: ha un linguaggio proprio. */}
        <div>
          <div className="data-hd">
            <span className="tk-sez-t">Dati del cliente</span>
          </div>
          <div className="ed-kv">
            {/* Email e telefono sempre in riga, anche vuoti: il buco si vede e
                si riempie, mentre una riga che sparisce non chiede niente a
                nessuno. Li porta su il sync FattureInCloud dove FIC li ha. */}
            <div className="ed-kv-row">
              <span className="k">Email</span>
              <span className="v">{c.email ? <a className="hover:underline" style={{ color: 'var(--accent)' }} href={`mailto:${c.email}`}>{c.email}</a> : '—'}</span>
            </div>
            <div className="ed-kv-row">
              <span className="k">Telefono</span>
              <span className="v">{c.phone ? <a className="hover:underline" style={{ color: 'var(--accent)' }} href={`tel:${c.phone.replace(/\s/g, '')}`}>{c.phone}</a> : '—'}</span>
            </div>
            {/* Un indirizzo si scrive con le virgole: senza, "26 95030" sembra
                un numero solo. La scheda contatto lo fa gia' cosi'. */}
            <div className="ed-kv-row"><span className="k">Sede</span><span className="v">{[c.addressLine, c.zip, c.city, c.province].filter(Boolean).join(', ') || '—'}</span></div>
            {c.website && <div className="ed-kv-row"><span className="k">Sito</span><span className="v"><a className="hover:underline" style={{ color: 'var(--accent)' }} href={c.website} target="_blank" rel="noreferrer">{c.website.replace(/^https?:\/\//, '')}</a></span></div>}
            {c.pec && <div className="ed-kv-row"><span className="k">PEC</span><span className="v">{c.pec}</span></div>}
          </div>
          {!showMargin && (
            <div className="flex items-center gap-1.5 pt-2 mute text-[13px]">
              <Icon name="lock" size={10} /> Marginalità · riservata
            </div>
          )}
          <ClientTagsSection clientId={c.id} tags={c.tags} />
        </div>

        {showMargin && margin && <><div className="ed-divider" /><ClientMarginPanel margin={margin} /></>}

        <div className="ed-divider" />
        <ContactsCard clientId={c.id} contacts={c.contacts} />

        <div className="ed-divider" />
        <PinnedNotesCard clientId={c.id} onOpenDiary={() => setTab('diario')} />
      </div>
      </div>
    </div>
  );
}

function ClientMarginPanel({ margin }: { margin: ClientMarginDTO }) {
  return (
    <div className="margin-panel">
      <div className="margin-panel-hd">
        <span className="tk-sez-t">Quanto ci resta, quest’anno</span>
        <span style={{ color: 'var(--mute)', display: 'inline-flex' }}><Icon name="eye" size={14} /></span>
      </div>
      <div className="margin-panel-bd">
        <div className="margin-row"><span className="label">Ricavi di quest’anno</span><span className="value">{euro(margin.revenue)}</span></div>
        <div className="margin-row"><span className="label">Costi dei suoi progetti</span><span className="value">{euro(margin.cost)}</span></div>
        <div className="margin-row"><span className="label">Incassato da sempre</span><span className="value">{euro(margin.paidInvoicesTotal)}</span></div>
        <div className="margin-highlight">
          <div className="tk-sez-t mb-1.5" style={{ color: 'var(--mute)', fontWeight: 500, fontSize: 13 }}>Margine lordo</div>
          <div className="big">{euro(margin.marginAbs)}</div>
          <div className="row between mt-2 text-[13px]" style={{ color: 'var(--mute)' }}>
            {/* Senza costi registrati il 100% non è un margine: si dice (giro del 26/10/2026, New Omnia). */}
            <span>{margin.cost > 0 ? `${fmtPct(margin.marginPct, 1)} dei ricavi` : 'nessun costo registrato'}</span>
            <span>{margin.activeProjectsCount} {margin.activeProjectsCount === 1 ? 'progetto in corso' : 'progetti in corso'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactsCard({ clientId, contacts }: { clientId: string; contacts: ContactDTO[] }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);

  const del = useMutation({
    mutationFn: (contactId: string) => api.delete(`/clients/contacts/${contactId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', clientId] }),
  });

  return (
    // Kicker-block NUDO: micro-titolo + azione ghost (la primaria resta nell'header pagina)
    <div>
      <div className="data-hd">
        <span className="tk-sez-t">Referenti</span>
        <button className="btn btn-sm btn-ghost" onClick={() => openModal('newContact', { clientId })}>
          <Icon name="plus" size={12} /> Nuovo
        </button>
      </div>
      {contacts.map((co) => (
        <div key={co.id} className="flex items-center gap-2.5 py-2.5 group">
          <Avatar initials={co.fullName.split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()} size={28} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold flex items-center gap-2">
              <span className="truncate">{co.fullName}</span>
              {co.isPrimary && <Chip tone="ghost">primario</Chip>}
            </div>
            <div className="detail-meta truncate" style={{ gap: 6 }}>
              {co.role && <span className="truncate">{co.role}</span>}
              {co.email && <>{co.role && <span className="mute-2">·</span>}<span className="truncate">{co.email}</span></>}
            </div>
          </div>
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
            <button className="btn btn-sm btn-ghost" onClick={() => openModal('newContact', { clientId, contact: co })} title="Modifica">
              <Icon name="more" size={10} />
            </button>
            <button className="btn btn-sm btn-ghost" onClick={async () => { if (await confirmDialog({ title: `Eliminare ${co.fullName}?`, danger: true, confirmLabel: 'Elimina referente' })) del.mutate(co.id); }} title="Elimina">
              <Icon name="x" size={10} />
            </button>
          </div>
        </div>
      ))}
      {!contacts.length && <div className="py-4 text-center text-[13px] mute">Nessun referente</div>}
    </div>
  );
}

function PinnedNotesCard({ clientId, onOpenDiary }: { clientId: string; onOpenDiary: () => void }) {
  const { data: notes } = useQuery({
    queryKey: ['client-notes', clientId],
    queryFn: () => api.get<ClientNoteDTO[]>(`/clients/${clientId}/notes`),
  });
  const pinned = (notes ?? []).filter((n) => n.pinned).slice(0, 2);

  return (
    // Kicker-block NUDO: il conteggio è testo mono, non badge; il rimando al Diario sta nel kicker
    <div>
      <div className="data-hd">
        <div className="flex items-baseline gap-2">
          <span className="tk-sez-t">Note in evidenza</span>
          <span className="mono mute text-[12px]">{pinned.length}</span>
        </div>
        <button className="text-text text-[13px] crm-hit" onClick={onOpenDiary}>Diario →</button>
      </div>
      {pinned.map((n) => (
        <div key={n.id} className="py-2.5 first:pt-0">
          <div className=" leading-relaxed whitespace-pre-wrap">{n.body}</div>
          <div className="mute-2 text-[12px] mono mt-1.5">
            {autoreNota(n)} · {fmtDate(n.createdAt)}
          </div>
        </div>
      ))}
      {!pinned.length && <div className="mute text-[13px] py-1">Nessuna nota in evidenza: nel Diario si fissa una nota per vederla qui.</div>}
    </div>
  );
}

// =====================================================================
// PROGETTI tab
// =====================================================================
function ProjectsTab({ c, showMargin }: { c: ClientFull; showMargin: boolean }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'attivi' | 'preventivo' | 'chiusi'>('all');

  const filtered = c.projects.filter((p) => {
    if (filter === 'attivi') return (ACTIVE_PROJECT_STATUSES as readonly string[]).includes(p.status);
    if (filter === 'preventivo') return p.status === 'PREVENTIVO';
    if (filter === 'chiusi') return ['CHIUSO', 'IN_PAUSA'].includes(p.status);
    return true;
  });

  return (
    <div className="space-y-4">
      {/* mb-4 + flex-wrap e' la forma canonica della barra filtri. Qui i 16px
          li dava gia' lo space-y-4 del contenitore: scritti anche sulla barra
          (i margini verticali fra fratelli collassano, restano 16) la barra
          non dipende piu' da chi la contiene. */}
      <MobileFilters attivi={filter !== 'all' ? 1 : 0}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="seg-control crm-seg">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>Tutti ({c.projects.length})</button>
          <button aria-pressed={filter === 'attivi'} onClick={() => setFilter('attivi')}>Attivi</button>
          <button aria-pressed={filter === 'preventivo'} onClick={() => setFilter('preventivo')}>Preventivi</button>
          <button aria-pressed={filter === 'chiusi'} onClick={() => setFilter('chiusi')}>Chiusi / In pausa</button>
        </div>
        <div className="spacer" />
        <span className="mono mute text-[12px]">{euro(filtered.reduce((a, p) => a + (Number(p.revenue) || 0), 0))} totali</span>
      </div>
      </MobileFilters>

      {/* Full-bleed: la tabella vive sul foglio, senza gabbia */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-baseline gap-2">
            <span className="tk-sez-t">Progetti</span>
            <span className="mono mute text-[12px]">{filtered.length}</span>
          </div>
        </div>
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th>Progetto</th><th>Divisione</th><th>Fase</th><th>PM</th>
              <th className="num">Progresso</th>
              <th className="num">Ricavi</th>
              {showMargin && <th className="num">Margine</th>}
              <th className="num">Scadenza</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const rev = Number(p.revenue) || 0;
              const cost = Number(p.cost) || 0;
              const pct = rev > 0 ? +(((rev - cost) / rev) * 100).toFixed(1) : 0;
              return (
                <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                  {/* Nome + codice su due righe: il codice arretra a secondaria */}
                  <td data-m="hd" className="cell-2l"><div className="p">{p.name}</div><div className="s mono">{p.code}</div></td>
                  <td data-m="key" data-l="Divisione"><DivisionTag division={p.division as any} /></td>
                  <td data-m="key" data-l="Fase"><Chip tone={statusTone(p.status)} dot>{p.phase}</Chip></td>
                  {/* Il PM senza il resto della squadra si legge nella scheda. */}
                  <td data-m="off">{p.pm ? `${p.pm.firstName[0]}. ${p.pm.lastName}` : '—'}</td>
                  <td data-m="row" data-l="Progresso" className="num crm-cell-progress" style={{ width: 120 }}>
                    <div className="flex items-center gap-2 justify-end">
                      <div className="barbar" style={{ width: 60 }}>
                        <span style={{ width: `${p.progress}%` }} />
                      </div>
                      <span className="mono mute text-[12px]">{p.progress}%</span>
                    </div>
                  </td>
                  <td data-m="key" data-l="Ricavi" className="num">{euro(rev)}</td>
                  {showMargin && <td data-m="key" data-l="Margine" className={`num tbl-metric ${cost > 0 && pct < 20 ? 'text-alert' : 'accent'}`} title={cost > 0 ? undefined : 'Nessun costo registrato su questo progetto: il margine non si può calcolare'}>{cost > 0 ? fmtPct(pct, 1) : <span className="mute-2">senza costi</span>}</td>}
                  <td data-m="key" data-l="Scadenza" className="num mono mute">{fmtDate(p.deadline)}</td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={showMargin ? 8 : 7} style={{ padding: 0 }}>
                <EmptyState icon="cube" title="Nessun progetto" description="Nessun progetto in questa categoria — cambia filtro o creane uno dal menu Nuovo." />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// FATTURE tab
// =====================================================================
function InvoicesTab({ c }: { c: ClientFull }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {/* Lista snella e NUDA: imponibile e IVA restano nel dettaglio fattura; qui decide il totale */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-baseline gap-2">
            <span className="tk-sez-t">Fatture</span>
            <span className="mono mute text-[12px]">{c.invoiceStats.count}</span>
          </div>
          {/* Detto chiaro: la tabella è un estratto, i numeri in cima no.
              Senza questa riga il conteggio e le righe si contraddicono. */}
          {c.invoiceStats.count > c.invoices.length && (
            <span className="mute text-[12px]">ultime {c.invoices.length} · i totali sono su tutte</span>
          )}
        </div>
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr><th>Numero</th><th>Progetto</th><th>Emissione</th><th>Scadenza</th><th>Pagata</th><th>Stato</th><th>Metodo</th><th className="num">Totale</th></tr>
          </thead>
          <tbody>
            {c.invoices.map((i) => (
              <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}>
                <td data-m="hd" className="mono">{i.number ?? 'BOZZA'}</td>
                <td data-m="key" data-l="Progetto" className="mono mute-2">{i.project?.code ?? '—'}</td>
                <td data-m="key" data-l="Emissione" className="mono mute">{fmtDate(i.issueDate)}</td>
                <td data-m="key" data-l="Scadenza" className="mono mute">{fmtDate(i.dueDate)}</td>
                {/* Data di pagamento e metodo: si leggono nella fattura. In
                    scheda contano numero, stato, scadenza e totale. */}
                <td data-m="off" className="mono mute">{fmtDate(i.paidDate)}</td>
                <td data-m="key" data-l="Stato"><Chip tone={statusTone(i.status)} dot>{String(i.status).replace('_', ' ').toLowerCase()}</Chip></td>
                {/* Etichetta informativa, non stato → niente pill */}
                <td data-m="off">{i.paymentMethod ? <span className="chip-label">{i.paymentMethod}</span> : '—'}</td>
                <td data-m="key" data-l="Totale" className="num tbl-metric">{euro(i.total)}</td>
              </tr>
            ))}
            {!c.invoices.length && (
              <tr><td colSpan={8} style={{ padding: 0 }}>
                <EmptyState icon="invoice" title="Nessuna fattura" description="Emetti la prima fattura per questo cliente dal menu Nuovo." />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// DOCUMENTI tab
// =====================================================================
function DocumentsTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);
  const { data } = useQuery({
    queryKey: ['client-documents', clientId],
    queryFn: () => api.get<{ items: DocumentDTO[] }>(`/documents?clientId=${clientId}`),
  });

  const del = useMutation({
    mutationFn: (docId: string) => api.delete(`/documents/${docId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client-documents', clientId] }),
  });

  const docs = data?.items ?? [];

  return (
    <div className="space-y-4">
      {/* Niente colonna "Tipo": l'estensione è già nel nome del file.
          Kicker + conteggio mono + CTA secondaria: la primaria resta il "+ Nuovo" nell'header pagina */}
      <div className="ed-section">
        <div className="data-hd">
          <div className="flex items-baseline gap-2">
            <span className="tk-sez-t">Documenti</span>
            <span className="mono mute text-[12px]">{docs.length}</span>
          </div>
          <button className="btn btn-sm" onClick={() => openModal('uploadDoc', { scope: 'client', scopeId: clientId })}>
            <Icon name="upload" /> Carica documento
          </button>
        </div>
        {/* tbl--card: sotto i 768px ogni riga diventa una scheda. */}
        <table className="tbl tbl--ed tbl--card">
          <thead>
            <tr>
              <th>Nome</th><th>Versione</th><th>Autore</th><th>Data</th>
              <th className="num">Dim.</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {/* Via i text-[12px] dai <td>: .tbl tbody td li batte per
                specificita', quindi non hanno mai cambiato niente. */}
            {docs.map((d) => (
              <tr key={d.id}>
                <td data-m="hd" className="font-medium"><Icon name="file" size={12} className="mute inline mr-1.5" />{d.name}</td>
                <td data-m="key" data-l="Versione" className="mono mute">{d.version ?? 'v1'}</td>
                <td data-m="key" data-l="Autore">{d.uploader ? <span className="">{d.uploader.firstName[0]}. {d.uploader.lastName}</span> : '—'}</td>
                <td data-m="key" data-l="Data" className="mono mute">{fmtDate(d.createdAt)}</td>
                <td data-m="key" data-l="Dimensione" className="num mono mute">{(d.size / 1024).toFixed(0)} KB</td>
                <td data-m="act">
                  <div className="row" style={{ gap: 2 }}>
                    <button className="btn btn-sm btn-ghost" title="Elimina"
                      onClick={async () => { if (await confirmDialog({ title: `Eliminare "${d.name}"?`, danger: true, confirmLabel: 'Elimina documento' })) del.mutate(d.id); }}>
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!docs.length && (
              <tr><td colSpan={6} style={{ padding: 0 }}>
                <EmptyState
                  icon="upload"
                  title="Nessun documento"
                  description="Carica contratti, brief e materiali sulla scheda cliente."
                  action={
                    <button className="btn btn-sm" onClick={() => openModal('uploadDoc', { scope: 'client', scopeId: clientId })}>
                      <Icon name="upload" size={12} /> Carica il primo
                    </button>
                  }
                />
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
// DIARIO tab — seg-control che monta i tab Comunicazioni/Note esistenti
// =====================================================================
function DiaryTab({ c }: { c: ClientFull }) {
  const [view, setView] = useState<'comunicazioni' | 'note'>('comunicazioni');

  return (
    <div className="space-y-4">
      {/* Qui la barra non nasconde un filtro ma sceglie cosa si guarda: resta
          in chiaro anche sul telefono, non dietro il tasto "Filtri". */}
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="seg-control crm-seg">
          <button aria-pressed={view === 'comunicazioni'} onClick={() => setView('comunicazioni')}>Comunicazioni ({c.communications.length})</button>
          <button aria-pressed={view === 'note'} onClick={() => setView('note')}>Note</button>
        </div>
        <div className="spacer" />
      </div>
      {view === 'comunicazioni' ? <CommunicationsTab c={c} /> : <NotesTab clientId={c.id} />}
    </div>
  );
}

// =====================================================================
// COMUNICAZIONI tab
// =====================================================================
function CommunicationsTab({ c }: { c: ClientFull }) {
  const [selected, setSelected] = useState<string | null>(c.communications[0]?.id ?? null);
  const openModal = useModalStore((s) => s.open);

  const active = c.communications.find((cm) => cm.id === selected) ?? c.communications[0];

  return (
    // gap-6 (24px) e non gap-4: e' il gutter delle due colonne di un dettaglio
    // in tutto il resto dell'app, compresa la griglia gemella 380px 1fr della
    // scheda progetto. Era l'unica a 16.
    <div className="grid gap-6 crm-comm" style={{ gridTemplateColumns: '380px 1fr' }}>
      {/* Lista thread NUDA: kicker + conteggio mono; le righe vivono sul foglio */}
      <div className="ed-section flex flex-col" style={{ maxHeight: 700 }}>
        <div className="data-hd">
          <div className="flex items-baseline gap-2">
            <span className="t-micro">Thread</span>
            <span className="mono mute text-[12px]">{c.communications.length}</span>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={() => openModal('newComm', { clientId: c.id })}>
            <Icon name="plus" size={12} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {c.communications.map((cm) => (
            <div key={cm.id}
              onClick={() => setSelected(cm.id)}
              className="px-4 py-3 border-b border-line cursor-pointer hover:bg-paper-2"
              style={{ background: active?.id === cm.id ? 'var(--paper-2)' : undefined, borderLeft: active?.id === cm.id ? '2px solid var(--accent)' : '2px solid transparent' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="t-micro">{cm.kind}</span>
                {cm.project && <span className="mono text-[12px] mute-2">{cm.project.code}</span>}
                <span className="spacer" />
                <span className="mono mute text-[12px]">{fmtDateShort(cm.occurredAt)}</span>
              </div>
              <div className="font-semibold truncate">{cm.subject}</div>
              {cm.body && <div className="text-[13px] mute truncate mt-0.5">{cm.body}</div>}
              {cm.tag && <div className="detail-meta mt-1 text-[12px]"><span className="mute-2">{cm.tag}</span></div>}
            </div>
          ))}
          {!c.communications.length && (
            <EmptyState icon="mail" title="Nessuna comunicazione" description="Registra call, email e meeting dal menu Nuovo." />
          )}
        </div>
      </div>

      {active ? (
        /* Pannello lettura NUDO: hairline verticale a separare le colonne, niente gabbia */
        <div className="ed-section pl-6 crm-comm-read" style={{ borderLeft: '1px solid var(--line)' }}>
          <div className="pb-4 border-b border-line">
            <h2 className="t-h3">{active.subject}</h2>
            <div className="detail-meta mt-2 truncate">
              <span>{active.kind.toLowerCase()}</span>
              {active.durationMin && <><span className="mute-2">·</span><span className="v">{active.durationMin} min</span></>}
              {active.tag && <><span className="mute-2">·</span><span>{active.tag}</span></>}
              {active.project && <><span className="mute-2">·</span><span className="mono v">{active.project.code}</span></>}
              <span className="mute-2">·</span>
              <span className="mono">{fmtDate(active.occurredAt)}</span>
            </div>
            {active.author && (
              <div className="flex items-center gap-2 mt-2 mute text-[13px]">
                <Avatar initials={active.author.initials} size={20} />
                {/* Scritta da MUNE (2D): l'autore resta chi ha chiesto, «Andrea Anfuso, tramite MUNE». */}
                <span>{chiHaScritto(`${active.author.firstName} ${active.author.lastName}`, active.aiActionId)}</span>
              </div>
            )}
          </div>
          <div className="py-4">
            {active.body ? (
              <div className=" leading-relaxed whitespace-pre-wrap">{active.body}</div>
            ) : (
              <div className="mute text-[13px] italic">Nessun corpo</div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid place-items-center text-mute text-[13px] pl-6 crm-comm-read" style={{ minHeight: 400, borderLeft: '1px solid var(--line)' }}>
          Seleziona una comunicazione
        </div>
      )}
    </div>
  );
}

// =====================================================================
// NOTE tab
// =====================================================================
function NotesTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const openModal = useModalStore((s) => s.open);

  const { data: notes } = useQuery({
    queryKey: ['client-notes', clientId],
    queryFn: () => api.get<ClientNoteDTO[]>(`/clients/${clientId}/notes`),
  });

  const togglePin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => api.patch(`/clients/notes/${id}`, { pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client-notes', clientId] }),
  });

  // 29/10/2026, fase 2D (decisione 40): la DELETE mette la nota nel cestino, e per 6 secondi
  // il toast la rimette (POST /clients/notes/:id/ripristina). Il ripristino chiama l'API da
  // sé, come in MemoriaVista: funziona anche se nel frattempo si è passati alle
  // Comunicazioni o a un'altra pagina. Al telefono le note si leggono e basta; una vista del
  // cestino non c'è.
  const ripristina = (id: string) =>
    api.post(`/clients/notes/${id}/ripristina`)
      .then(() => qc.invalidateQueries({ queryKey: ['client-notes', clientId] }))
      .catch((e: any) => toast.alert('Nota non ripristinata', e?.message ?? 'Riprova'));

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/clients/notes/${id}`),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ['client-notes', clientId] });
      toast.undo('Nota eliminata', { onUndo: () => { void ripristina(id); } });
    },
    onError: (e: any) => toast.alert('Nota non eliminata', e?.message ?? 'Riprova'),
  });

  return (
    /* Kicker + conteggio mono + CTA secondaria; le note restano card: sono tessere di contenuto */
    <div className="ed-section">
      <div className="data-hd">
        <div className="flex items-baseline gap-2">
          <span className="tk-sez-t">Note</span>
          <span className="mono mute text-[12px]">{notes?.length ?? 0}</span>
        </div>
        {/* Secondaria: l'unica primaria della vista resta il "+ Nuovo" nell'header pagina */}
        <button className="btn btn-sm" onClick={() => openModal('newClientNote', { clientId })}>
          <Icon name="plus" size={12} /> Nuova nota
        </button>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {(notes ?? []).map((n) => (
          <div key={n.id} className="card group">
            <div className="px-4 pt-3 pb-2 flex items-start gap-2 border-b border-line">
              {n.author && <Avatar initials={n.author.initials} size={22} />}
              <div className="flex-1 min-w-0">
                {/* Scritta da MUNE la firma va a capo invece di tagliarsi (29/10/2026, dalla revisione
                    della 2D): col pannello di MUNE aperto la scheda è stretta, e «Andrea Anfuso,
                    tramite MUNE» diventava «Andrea Anfuso, tr…» proprio dopo una nota di MUNE. */}
                <div className={`font-semibold text-[13px] ${n.aiActionId ? 'break-words' : 'truncate'}`}>{autoreNota(n)}</div>
                <div className="mute-2 text-[12px] mono">{fmtDate(n.createdAt)}</div>
              </div>
              {n.pinned && <Chip tone="ghost"><Icon name="pin" size={10} /> In evidenza</Chip>}
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
                <button className="btn btn-sm btn-ghost" onClick={() => togglePin.mutate({ id: n.id, pinned: !n.pinned })} title={n.pinned ? 'Togli dall’evidenza' : 'Metti in evidenza'}>
                  <Icon name="pin" size={10} />
                </button>
                <button className="btn btn-sm btn-ghost" onClick={async () => { if (await confirmDialog({ title: 'Eliminare nota?', danger: true, confirmLabel: 'Elimina nota' })) del.mutate(n.id); }} title="Elimina">
                  <Icon name="x" size={10} />
                </button>
              </div>
            </div>
            <div className="card-bd">
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap">{n.body}</div>
            </div>
          </div>
        ))}
        {!notes?.length && (
          <div className="col-span-2">
            <EmptyState
              icon="pin"
              title="Nessuna nota"
              description='Usa "+ Nuova nota" per annotare informazioni rilevanti sul cliente.'
              action={
                <button className="btn btn-primary" onClick={() => openModal('newClientNote', { clientId })}>
                  <Icon name="plus" size={12} /> Nuova nota
                </button>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// TAGS — coda del blocco Anagrafica, NUDA sul foglio (ex sezione card)
// =====================================================================
function ClientTagsSection({ clientId, tags }: { clientId: string; tags: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');

  const setTags = useMutation({
    mutationFn: (next: string[]) => api.patch(`/clients/${clientId}`, { tags: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId] });
      qc.invalidateQueries({ queryKey: ['clients'] });
      toast.ok('Tag aggiornati');
    },
    onError: (e: any) => toast.alert('Errore', e?.message ?? 'Riprova'),
  });

  const submit = () => {
    const v = value.trim();
    if (!v) return;
    if (tags.some((t) => t.name.toLowerCase() === v.toLowerCase())) {
      toast.warn('Tag già presente');
      return;
    }
    setTags.mutate([...tags.map((t) => t.name), v]);
    setAdding(false);
    setValue('');
  };
  const remove = (name: string) => setTags.mutate(tags.filter((t) => t.name !== name).map((t) => t.name));

  return (
    // 16 e non 20: la griglia del progetto e' 4/8/12/16/24, 20 non e' un
    // gradino (mt-5 era l'unico dell'intera schermata).
    <div className="mt-4">
      <div>
        <div className="tk-sez-t mb-2">Etichette</div>
        <div className="flex flex-wrap gap-1.5 items-center">
        {tags.map((t) => (
          <span key={t.id} className="group inline-flex items-center gap-1">
            <Chip tone="ghost">{t.name}</Chip>
            <button
              className="opacity-0 group-hover:opacity-100 transition mute hover:text-alert"
              title="Rimuovi tag"
              onClick={() => remove(t.name)}
            >
              <Icon name="x" size={10} />
            </button>
          </span>
        ))}
        {adding ? (
          <span className="inline-flex items-center gap-1">
            <input
              className="inp text-[12px] py-0.5"
              style={{ width: 110 }}
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') { setAdding(false); setValue(''); }
              }}
              placeholder="Nuovo tag…"
            />
            <button className="btn btn-sm btn-ghost" onClick={submit} disabled={setTags.isPending}>
              <Icon name="check" size={10} />
            </button>
          </span>
        ) : (
          <button className="chip ghost crm-hit" onClick={() => setAdding(true)} title="Aggiungi tag">
            <Icon name="plus" size={10} /> tag
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

